import { useEffect, useState, useMemo, useRef, Fragment } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
    Repeat, PauseCircle, PlayCircle, Calendar, Clock, Plus, LinkSimple,
    CaretRight, Eye, Pencil, Lightning, Receipt, MagnifyingGlass, Archive,
    Lock, LockOpen, Warning, X, Cube, Sparkle, Wrench,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { formatINR, formatDate } from "../lib/format";
import PageHeader from "../components/layout/PageHeader";
import EmptyState from "../components/EmptyState";
import StatusBadge from "../components/StatusBadge";
import RoleGuard from "../components/RoleGuard";
import CreateManualAmcDialog from "../components/CreateManualAmcDialog";
import EditManualAmcDialog from "../components/EditManualAmcDialog";
import ExtendAmcEndDateDialog from "../components/ExtendAmcEndDateDialog";
import InitiateRenewalDialog from "../components/InitiateRenewalDialog";
import GenerateAmcInvoiceNowDialog from "../components/GenerateAmcInvoiceNowDialog";
import RecordOfflineInvoiceDialog from "../components/RecordOfflineInvoiceDialog";
import CloseAmcDialog from "../components/CloseAmcDialog";
import { PinnedScrollbar } from "../components/PinnedScrollbar";
import { StickyTableHeader } from "../components/StickyTableHeader";
import {
    ShowArchivedToggle, ArchivedBadge, ArchiveRowButton, UnarchiveRowButton, useArchiveAction,
} from "../components/ArchiveControls";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

// Iter 59 — derived effective_status tones.
//   Active         — green / "Active"
//   Upcoming       — slate / "Open"
//   Paused         — amber / "Open"
//   Calendar Ended — amber / "Open"  (advisory only; schedule remains billable)
//   Closed         — slate / "Unpaid"  (operator-locked, hard block)
// POAGG-AMC-001 Phase 3 (2026-02-15) — Source-type & creation-method
// presentation maps. Three source types are first-class:
//   - po_linked     : Phase 2 D5 — manual schedule created from PO dialog
//   - po_derived    : Legacy auto-cascade from atomic AMC line on PO finalize
//   - manual        : Standalone manual schedule (no PO linkage)
//
// Two creation methods carry through:
//   - auto_finalize    : po_derived rows created at PO finalize cascade
//   - manual_from_po   : po_linked rows from Phase 2 CreateAmcFromPoDialog
//   - manual_standalone: manual rows from CreateManualAmcDialog
const SOURCE_TYPE_META = {
    po_linked: {
        label: "PO-Linked",
        Icon: Lightning,
        title: "Manually activated from a finalized PO via the AMC Pool panel",
        cls: "bg-violet-50 text-violet-700 border-violet-200",
    },
    po_derived: {
        label: "PO-Derived",
        Icon: LinkSimple,
        title: "Auto-cascaded from a finalized PO line item (legacy atomic-AMC path)",
        cls: "bg-indigo-50 text-indigo-700 border-indigo-200",
    },
    manual: {
        label: "Manual",
        Icon: Cube,
        title: "Standalone manual schedule — no PO linkage (legacy / migrated)",
        cls: "bg-amber-50 text-amber-700 border-amber-200",
    },
};
const CREATION_METHOD_META = {
    auto_finalize: { label: "Auto", Icon: Sparkle, title: "Auto-created at PO finalize" },
    manual_from_po: { label: "From PO", Icon: Lightning, title: "Created via AMC Pool → Activate AMC" },
    manual_standalone: { label: "Standalone", Icon: Wrench, title: "Created via Create Manual AMC" },
};

const STATUS_TONE = {
    "Active": { status: "Active" },
    "Upcoming": { status: "Open" },
    "Paused": { status: "Open" },
    "Calendar Ended": { status: "Open" },
    "Closed": { status: "Unpaid" },
    // Iter 59 back-compat — older caches may briefly show "Expired"; mirror Calendar Ended.
    "Expired": { status: "Open" },
};

// Iter 36 / Iter 59: read calendar-derived effective_status with safe fallback.
const effectiveStatus = (s) => s.effective_status || s.status;

// ─────────────────────────────────────────────────────────────────────
// Iter 2026-02 · AMC Cycle Expansion (operator directive 2026-02-16).
//
// Operators want to see each AMC schedule broken out per due-cycle
// (Nuvama Quarterly → 4 rows; MFSL Half-Yearly → 2 rows) so they can
// review each cycle's status at a glance. Backend already stores the
// cycle count implicitly via `generated_count` + `start_date` +
// `end_date` + `billing_frequency` — the expansion is a pure
// client-side derivation. Aligned with the backend's `_advance`
// helper (routes_amc.py:148) which uses fixed day deltas.
// ─────────────────────────────────────────────────────────────────────
const FREQ_DELTA_DAYS = {
    "Annual": 365,
    "Half-Yearly": 182,
    "Quarterly": 91,
    "Monthly": 30,
};

const MONTHS_PER_CYCLE = {
    "Annual": 12,
    "Half-Yearly": 6,
    "Quarterly": 3,
    "Monthly": 1,
};

/** Annualise the schedule.amount using the same rule as backend
 *  `_annualised_excl_gst` (routes_amc.py:1478). Returns 0 for
 *  malformed rows so caller can skip cleanly. */
function annualisedAmount(sched) {
    const amt = Number(sched.amount || 0);
    if (amt <= 0) return 0;
    const basis = sched.value_basis || "annual";
    if (basis === "annual") return amt;
    const mpc = MONTHS_PER_CYCLE[sched.billing_frequency] || 12;
    return amt * (12 / mpc);
}

/** Per-cycle amount (annual / cycles_per_year). Rounded to 2 dp. */
function perCycleAmount(sched) {
    const ann = annualisedAmount(sched);
    if (ann <= 0) return 0;
    const mpc = MONTHS_PER_CYCLE[sched.billing_frequency] || 12;
    return Math.round(ann * (mpc / 12) * 100) / 100;
}

/* Annual Cost subscriptions bill on calendar months (backend
   advance_for_schedule) and the last instalment of each contract year
   absorbs the rounding remainder (backend subscription_instalment). */
const DAY_MS_SUB = 86400000;
function addMonthsUtc(ms, months, anchorDay) {
    const d = new Date(ms);
    const y = d.getUTCFullYear() + Math.floor((d.getUTCMonth() + months) / 12);
    const m = (d.getUTCMonth() + months) % 12;
    const last = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
    return Date.UTC(y, m, Math.min(anchorDay, last));
}
function subscriptionInstalment(sched, idx) {
    const ann = annualisedAmount(sched);
    const mpc = MONTHS_PER_CYCLE[sched.billing_frequency] || 12;
    const n = 12 / mpc;
    const base = Math.round((ann / n) * 100) / 100;
    return (idx - 1) % n === n - 1 ? Math.round((ann - base * (n - 1)) * 100) / 100 : base;
}

/** Expand a schedule into its individual cycles. Mirrors the backend
 *  scheduler's cycle walk: `period_start` advances by
 *  FREQ_DELTA_DAYS each iteration; `period_end` = next start − 1 day
 *  (clamped to schedule end). Returns [] if start/end are malformed. */
function expandCycles(sched) {
    if (!sched?.start_date || !sched?.end_date) return [];
    const startMs = Date.parse(sched.start_date);
    const endMs = Date.parse(sched.end_date);
    if (isNaN(startMs) || isNaN(endMs) || startMs > endMs) return [];
    const delta = FREQ_DELTA_DAYS[sched.billing_frequency] || 365;
    const DAY_MS = DAY_MS_SUB;
    const cycles = [];
    const isSub = sched.kind === "subscription";
    const anchorDay = Number(sched.billing_day) || new Date(startMs).getUTCDate();
    const mpc = MONTHS_PER_CYCLE[sched.billing_frequency] || 12;
    const generated = Number(sched.generated_count || 0);
    const today = Date.now();
    let cursor = startMs;
    let idx = 1;
    const SAFETY_CAP = 200;
    while (cursor <= endMs && idx <= SAFETY_CAP) {
        const rawEnd = isSub
            ? addMonthsUtc(startMs, mpc * idx, anchorDay) - DAY_MS
            : cursor + (delta - 1) * DAY_MS;
        const cycleEnd = rawEnd > endMs ? endMs : rawEnd;
        // Cycle status:
        //   Invoiced  — index ≤ generated_count (already billed)
        //   Due       — not yet invoiced AND today falls within this cycle
        //   Overdue   — not yet invoiced AND cycle end has passed
        //   Upcoming  — not yet invoiced AND cycle start is in the future
        let status;
        if (idx <= generated) {
            status = "Invoiced";
        } else if (today > cycleEnd) {
            status = "Overdue";
        } else if (today >= cursor) {
            status = "Due";
        } else {
            status = "Upcoming";
        }
        cycles.push({
            cycle_number: idx,
            period_start: new Date(cursor).toISOString().slice(0, 10),
            period_end: new Date(cycleEnd).toISOString().slice(0, 10),
            status,
            amount: isSub ? subscriptionInstalment(sched, idx) : undefined,
        });
        cursor = cycleEnd + DAY_MS;
        idx += 1;
    }
    return cycles;
}

const CYCLE_STATUS_META = {
    Invoiced: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Invoiced" },
    Due:      { cls: "bg-amber-50 text-amber-800 border-amber-200",      label: "Due" },
    Overdue:  { cls: "bg-rose-50 text-rose-700 border-rose-200",         label: "Overdue" },
    Upcoming: { cls: "bg-slate-50 text-slate-600 border-slate-200",      label: "Upcoming" },
};

/** ISO string → epoch ms (safe against nulls). */
function _iso(v) {
    if (!v) return null;
    const ms = Date.parse(v);
    return isNaN(ms) ? null : ms;
}

/** Iter 2026-02 · Correct Next-Due display (Option B, user-directed 2026-02-16).
 *
 *  Rule: start from the natural next-cycle date
 *  (``start_date + generated_count × FREQ_DELTA``) and roll it forward
 *  by one cycle at a time while it is still in the past. If we walk
 *  past the schedule's end date, cap at ``end_date + 1 day`` — the
 *  renewal date.
 *
 *  Rationale: operators think of "Next Due" as a **forward-looking**
 *  date — the next billable action. When ``AMC_AUTO_GENERATION`` is
 *  disabled (moratorium) or historical periods were billed off-system,
 *  we don't want the column stuck on an in-the-past cycle start.
 *
 *  Examples:
 *    - Darashaw (Annual, period Oct 25 → Sep 26, Gen=0, today Feb 26)
 *      → computed Oct 25 (past) → +365 = Oct 26 (future) → renewal
 *      date shown.
 *    - Nuvama (Quarterly, period Apr 26 → Mar 27, Gen=2, today Feb 26)
 *      → computed Oct 26 (Q3 start, already future) → Q3 shown.
 *    - Schedule fully in the past (period Apr 25 → Mar 26, Gen=0)
 *      → cycles roll through Q1..Q4 all in past → cap at Apr 26
 *      (renewal, still marked Overdue if today > that).
 *
 *  If backend ``next_due_date`` is *strictly later* than the computed
 *  value (legit operator override) we honour it.
 */
function effectiveNextDue(sched) {
    if (!sched?.start_date) return null;
    const startMs = _iso(sched.start_date);
    if (startMs == null) return null;
    const gen = Number(sched.generated_count || 0);
    const delta = FREQ_DELTA_DAYS[sched.billing_frequency] || 365;
    const DAY_MS = 86400000;
    const today = Date.now();
    const endMs = _iso(sched.end_date);

    let computed = startMs + gen * delta * DAY_MS;

    // Roll past cycles forward one by one while still inside the
    // schedule's contract window. Safety cap of 400 iterations
    // (max ≈ 33 years of monthly cycles) so a corrupt row can't
    // hang the browser.
    if (endMs != null) {
        let guard = 0;
        while (computed < today && computed <= endMs && guard < 400) {
            computed += delta * DAY_MS;
            guard += 1;
        }
        // If we walked past the contract end, cap at the renewal date
        // (end_date + 1 day) so we never point beyond the AMC term.
        const renewalMs = endMs + DAY_MS;
        if (computed > renewalMs) computed = renewalMs;
    }

    const backend = _iso(sched.next_due_date);
    return backend != null && backend > computed ? backend : computed;
}

/** Sort weight for the "Due Now first" order:
 *  Overdue schedules (next_due < today) come first, ordered by
 *  earliest first (most overdue at top). Due-now / upcoming follow
 *  chronologically. Closed / archived schedules sink to the bottom.
 */
function scheduleUrgencyKey(sched) {
    const effStatus = sched.effective_status || sched.status;
    if (effStatus === "Closed" || sched.deleted_at) return Number.MAX_SAFE_INTEGER;
    const nd = effectiveNextDue(sched);
    return nd == null ? Number.MAX_SAFE_INTEGER - 1 : nd;
}

export default function AMC() {
    const tableScrollRef = useRef(null);
    const [items, setItems] = useState([]);
    const [pipeline, setPipeline] = useState(null);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    // Iter 63 — global AMC auto-generation kill-switch banner state.
    // Fetched from /scheduler/amc-auto-generation (open to any authed user)
    // so Accounts + Sales operators on the AMC page see the moratorium
    // banner even though they lack `scheduler:run` permission.
    const [autoGen, setAutoGen] = useState(null);
    // Iter 37: per-row dialogs (edit / generate-now / record-offline)
    const [editSched, setEditSched] = useState(null);
    const [generateSched, setGenerateSched] = useState(null);
    const [offlineSched, setOfflineSched] = useState(null);
    // C.3 Commit 3: per-row dialog for Controlled Extension on
    // PO-derived schedules (§4.1).
    const [extendSched, setExtendSched] = useState(null);
    // C.3 Commit 4: per-row dialog for Renewal Lifecycle initiation.
    const [renewalSched, setRenewalSched] = useState(null);
    // Iter 59: per-row dialog for Close / Reopen (Primary Admin only).
    const [closeSched, setCloseSched] = useState(null);
    // Iter 32: inline preview state — { [scheduleId]: "loading" | "error" | dataObj | null }
    const [expandedId, setExpandedId] = useState(null);
    const [previews, setPreviews] = useState({});
    // Iter 45: client-side text search — same UX contract as the Clients
    // module (operators frequently jump Clients → AMC → Payments and need
    // consistent quick-filter behavior).
    const [textQuery, setTextQuery] = useState("");
    // Iter 2026-02 · Cycle Expansion — operator toggle to switch between
    // one-row-per-schedule (default, compact) and one-row-per-cycle
    // (Nuvama Quarterly → 4 rows). Client-side only; no backend changes.
    const [viewMode, setViewMode] = useState("schedules"); // "schedules" | "cycles"
    const { user } = useAuth();
    // POAGG-AMC-001 Phase 3 (2026-02-15) — URL-driven filters for deep links
    // from PO Detail ("View AMC schedules from this PO") and external mailers.
    // Server-side filter for po_id; client-side filter for source_type.
    const [searchParams, setSearchParams] = useSearchParams();
    const poFilter = searchParams.get("po_id") || "";
    const sourceFilter = searchParams.get("source_type") || "";
    const clearPoFilter = () => {
        const next = new URLSearchParams(searchParams);
        next.delete("po_id"); setSearchParams(next, { replace: true });
    };
    const setSourceFilterParam = (val) => {
        const next = new URLSearchParams(searchParams);
        if (val) next.set("source_type", val); else next.delete("source_type");
        setSearchParams(next, { replace: true });
    };
    // Iter 27.1: Manual / Legacy AMC creation is intentionally restricted to
    // super_admin during the initial operational rollout. Downstream AMC
    // visibility & pause/resume remain on po:write.
    const canCreateManual = user?.role === "super_admin";
    // Iter 37 RBAC (matches backend gates):
    //   - Edit manual schedule: Super Admin only
    //   - Generate Invoice Now: Super Admin + Accounts
    //   - Record Offline Invoice: Super Admin + Accounts
    const canEditManual = user?.role === "super_admin";
    const canBillOps = user?.role === "super_admin" || user?.role === "accounts";
    // Iter 51.2 — Archive UI is locked to the primary super-admin only.
    // canEditManual still gates non-archive UX (Create / Edit manual AMC) at the
    // role level, so secondary super_admins retain those capabilities.
    const isPrimaryAdmin = !!user?.is_primary_admin;
    // Iter 51.1 — Soft-delete (Archive) using the shared hook
    const [showArchived, setShowArchived] = useState(false);
    const archive = useArchiveAction({
        entityType: "AMC",
        archiveUrl: (row) => `/billing/amc/${row.id}/archive`,
        unarchiveUrl: (row) => `/billing/amc/${row.id}/unarchive`,
        onDone: () => load(),
        describe: (row) => (
            <>
                <div><span className="reedos-eyebrow">AMC</span>{" "}
                    <span className="font-mono text-slate-900">{row.amc_name}</span></div>
                <div className="mt-1"><span className="reedos-eyebrow">Client</span>{" "}
                    <span className="text-slate-800">{row.client?.name || "—"}</span></div>
                <div className="mt-1"><span className="reedos-eyebrow">Status</span>{" "}
                    <span className="text-slate-800">{row.status}</span></div>
            </>
        ),
    });

    const load = async () => {
        try {
            const listParams = {};
            if (showArchived) listParams.include_archived = true;
            // POAGG-AMC-001 Phase 3 (2026-02-15) — pass server-side po_id
            // filter when present so the API does the heavy lifting on
            // large AMC volumes (no need to fetch + client-filter).
            if (poFilter) listParams.po_id = poFilter;
            const [list, pipe, ag] = await Promise.all([
                api.get("/billing/amc/list", { params: listParams }),
                api.get("/billing/amc/_/pipeline"),
                api.get("/billing/scheduler/amc-auto-generation").catch(() => ({ data: null })),
            ]);
            setItems(list.data.items || []);
            setPipeline(pipe.data);
            setAutoGen(ag.data);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load AMC"));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [showArchived, poFilter]);

    // Iter 45 + POAGG-AMC-001 Phase 3: multi-field client-side filter —
    // mirrors Clients.jsx contract. Searches across product name, client
    // name, source type, creation method, frequency, client PO number,
    // PO id and any line-item product names. Also honours the `source_type`
    // URL chip (server already handled `po_id` if set).
    const visibleItems = useMemo(() => {
        const q = textQuery.trim().toLowerCase();
        let base = items;
        if (sourceFilter) {
            base = base.filter((s) => {
                // Legacy rows without source_type implicitly = po_derived.
                const effSrc = s.source_type || "po_derived";
                return effSrc === sourceFilter;
            });
        }
        if (q) {
            base = base.filter((s) => {
                const product = (s.amc_name || s.product_name || "").toLowerCase();
                const client = (s.client?.name || "").toLowerCase();
                const freq = (s.billing_frequency || "").toLowerCase();
                const effSrc = s.source_type || "po_derived";
                const sourceMeta = SOURCE_TYPE_META[effSrc] || SOURCE_TYPE_META.po_derived;
                const sourceTokens = `${effSrc} ${sourceMeta.label}`.toLowerCase();
                const cmMeta = CREATION_METHOD_META[s.creation_method] || {};
                const cmTokens = `${s.creation_method || ""} ${cmMeta.label || ""}`.toLowerCase();
                const poId = (s.po_id || "").toLowerCase();
                const poNum = (s.po?.po_number || "").toLowerCase();
                const extPo = (s.external_po_number || "").toLowerCase();
                const status = (s.effective_status || s.status || "").toLowerCase();
                const lineNames = (s.line_items || [])
                    .map((li) => (li.product_name || "").toLowerCase())
                    .join(" ");
                return (
                    product.includes(q) || client.includes(q) || freq.includes(q) ||
                    sourceTokens.includes(q) || cmTokens.includes(q) ||
                    poId.includes(q) || poNum.includes(q) || extPo.includes(q) ||
                    status.includes(q) || lineNames.includes(q)
                );
            });
        }
        // Iter 2026-02 · Default sort — "Due Now first". Ascending by
        // effective next-due date so overdue schedules float to the top,
        // then due-soon, then upcoming; Closed / archived sink to the
        // bottom via the sentinel keys in `scheduleUrgencyKey`.
        return [...base].sort((a, b) => scheduleUrgencyKey(a) - scheduleUrgencyKey(b));
    }, [items, textQuery, sourceFilter]);

    const togglePreview = async (sched) => {
        const id = sched.id;
        if (expandedId === id) {
            setExpandedId(null);
            return;
        }
        setExpandedId(id);
        if (previews[id] && previews[id] !== "error") return;  // cache
        setPreviews((p) => ({ ...p, [id]: "loading" }));
        try {
            const { data } = await api.get(`/billing/amc/${id}/preview-next-invoice`);
            setPreviews((p) => ({ ...p, [id]: data }));
        } catch (err) {
            setPreviews((p) => ({ ...p, [id]: "error" }));
            toast.error(formatApiError(err, "Failed to load preview"));
        }
    };

    const toggle = async (s) => {
        try {
            const action = s.status === "Active" ? "pause" : "resume";
            await api.post(`/billing/amc/${s.id}/${action}`);
            toast.success(`Schedule ${action}d`);
            await load();
        } catch (err) {
            toast.error(formatApiError(err, "Failed"));
        }
    };

    return (
        <div data-testid="amc-page">
            <PageHeader
                eyebrow="Phase 2"
                title="AMC pipeline"
                description="System-derived AMCs flow from finalized PO line items. Manual / Legacy AMCs can be created directly for existing relationships. Both share the same scheduler — invoices are auto-generated on each due date."
                actions={
                    canCreateManual && (
                        <Button
                            onClick={() => setCreateOpen(true)}
                            data-testid="amc-create-btn"
                            size="lg"
                            className="gap-2 bg-amber-600 hover:bg-amber-500 text-white shadow-sm font-semibold"
                        >
                            <Plus size={16} weight="bold" /> Create Manual AMC
                        </Button>
                    )
                }
            />
            <div className="px-6 sm:px-8 py-8 space-y-6">
                {/* Iter 63 — Global AMC auto-generation moratorium banner.
                    Only renders when the kill switch is ON (auto-generation
                    DISABLED). When enabled (default), the banner stays
                    invisible to keep the page calm. */}
                {autoGen && autoGen.enabled === false && (
                    <div
                        className="reedos-card p-5 border-rose-300 bg-rose-50/70"
                        data-testid="amc-auto-generation-moratorium-banner"
                    >
                        <div className="flex items-start gap-3">
                            <div className="shrink-0 mt-0.5">
                                <Warning size={22} weight="fill" className="text-rose-600" />
                            </div>
                            <div className="flex-1 space-y-1.5">
                                <div className="flex items-center gap-2">
                                    <span className="font-semibold text-rose-900 text-base">
                                        AMC auto invoice generation is DISABLED system-wide
                                    </span>
                                    <span className="inline-flex items-center rounded bg-rose-100 border border-rose-200 text-rose-800 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                                        Moratorium active
                                    </span>
                                </div>
                                <div className="text-sm text-rose-900/90 leading-relaxed">
                                    The 15-minute scheduler tick is paused. No AMC invoices will be auto-emitted
                                    against any schedule, regardless of due date. <strong>Operators must explicitly bill
                                    each contract</strong> via the per-row{" "}
                                    <span className="font-mono text-xs bg-white border border-rose-200 px-1.5 py-0.5 rounded">Generate Invoice Now</span>{" "}
                                    action below. Auto-reminders and weekly digest are unaffected.
                                </div>
                                <div className="text-xs text-rose-800/70 pt-0.5">
                                    Env var: <span className="font-mono">{autoGen.env_var}={autoGen.raw_value}</span>
                                    {" · "}Flip to <span className="font-mono">enabled</span> and restart the backend to resume.
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {pipeline && (
                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="amc-pipeline-stats">
                        <div className="reedos-card p-5">
                            <div className="reedos-eyebrow">Active schedules</div>
                            <div className="font-display text-3xl font-bold mt-2 reedos-data">{pipeline.active}</div>
                        </div>
                        <div className={"reedos-card p-5 " + (pipeline.due_now.count > 0 ? "border-amber-200 bg-amber-50/40" : "")}>
                            <div className="reedos-eyebrow flex items-center gap-1.5"><Clock size={12} /> Due now</div>
                            <div className={"font-display text-3xl font-bold mt-2 reedos-data " + (pipeline.due_now.count > 0 ? "text-amber-700" : "")}>{pipeline.due_now.count}</div>
                            <div className="text-xs text-slate-500 mt-1">{formatINR(pipeline.due_now.amount, { compact: true })} ready to invoice</div>
                        </div>
                        <div className="reedos-card p-5">
                            <div className="reedos-eyebrow flex items-center gap-1.5"><Calendar size={12} /> Upcoming 30 days</div>
                            <div className="font-display text-3xl font-bold mt-2 reedos-data">{pipeline.upcoming_30d.count}</div>
                            <div className="text-xs text-slate-500 mt-1">{formatINR(pipeline.upcoming_30d.amount, { compact: true })}</div>
                        </div>
                        <div className="reedos-card p-5" data-testid="amc-pipeline-calendar-ended">
                            <div className="reedos-eyebrow">Calendar ended</div>
                            <div className="font-display text-3xl font-bold mt-2 reedos-data text-amber-700">{pipeline.calendar_ended ?? pipeline.expired ?? 0}</div>
                            <div className="text-[11px] text-slate-500 mt-1">Advisory only · still billable</div>
                        </div>
                        <div className="reedos-card p-5" data-testid="amc-pipeline-closed">
                            <div className="reedos-eyebrow flex items-center gap-1.5"><Lock size={12} /> Closed</div>
                            <div className="font-display text-3xl font-bold mt-2 reedos-data text-slate-500">{pipeline.closed ?? 0}</div>
                            <div className="text-[11px] text-slate-500 mt-1">Operator-locked · no further billing</div>
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className="text-sm text-slate-500">Loading AMC schedules...</div>
                ) : items.length === 0 ? (
                    <EmptyState
                        icon={<Repeat size={40} weight="thin" />}
                        title="No AMC schedules yet"
                        description="Add an AMC line item to a finalized PO and a schedule will appear automatically — or create a Manual / Legacy AMC for an existing client."
                        data-testid="amc-empty"
                        action={
                            canCreateManual && (
                                <Button
                                    onClick={() => setCreateOpen(true)}
                                    data-testid="amc-empty-create-btn"
                                    size="lg"
                                    className="gap-2 bg-amber-600 hover:bg-amber-500 text-white shadow-sm font-semibold"
                                >
                                    <Plus size={16} weight="bold" /> Create Manual AMC
                                </Button>
                            )
                        }
                    />
                ) : (
                    <>
                    {/* Iter 45 + POAGG-AMC-001 Phase 3: search bar + filter chips */}
                    <div className="flex flex-wrap items-center gap-3" data-testid="amc-search-bar">
                        <div className="relative flex-1 min-w-[260px] max-w-md">
                            <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input
                                value={textQuery}
                                onChange={(e) => setTextQuery(e.target.value)}
                                placeholder="Search by product, client, frequency, source, Client PO or status…"
                                className="pl-8 h-9 text-sm"
                                data-testid="amc-search-input"
                            />
                        </div>
                        {/* POAGG-AMC-001 Phase 3 — Source type filter */}
                        <select
                            value={sourceFilter}
                            onChange={(e) => setSourceFilterParam(e.target.value)}
                            className="h-9 rounded-md border border-slate-200 bg-white px-2 text-xs focus:outline-none focus:ring-2 focus:ring-slate-300"
                            data-testid="amc-source-filter"
                            title="Filter by source type"
                        >
                            <option value="">All sources</option>
                            <option value="po_linked">PO-Linked (manual from PO)</option>
                            <option value="po_derived">PO-Derived (auto-cascaded)</option>
                            <option value="manual">Manual / Standalone</option>
                        </select>
                        {/* POAGG-AMC-001 Phase 3 — PO filter chip (URL-driven) */}
                        {poFilter && (
                            <span
                                className="inline-flex items-center gap-1.5 h-9 rounded-full bg-violet-50 text-violet-800 border border-violet-200 px-3 text-xs font-semibold"
                                data-testid="amc-po-filter-chip"
                            >
                                <Lightning size={12} weight="fill" />
                                <span>Filtered by PO:</span>
                                <Link
                                    to={`/purchase-orders/${poFilter}`}
                                    className="font-mono underline-offset-2 hover:underline"
                                    data-testid="amc-po-filter-chip-link"
                                >
                                    {items[0]?.po?.po_number || poFilter}
                                </Link>
                                <button
                                    type="button"
                                    onClick={clearPoFilter}
                                    className="ml-1 inline-flex items-center justify-center hover:text-violet-900"
                                    aria-label="Clear PO filter"
                                    data-testid="amc-po-filter-chip-clear"
                                >
                                    <X size={12} weight="bold" />
                                </button>
                            </span>
                        )}
                        {textQuery && (
                            <span className="text-xs text-slate-500" data-testid="amc-search-count">
                                {visibleItems.length} of {items.length}
                            </span>
                        )}
                        {!textQuery && (sourceFilter || poFilter) && (
                            <span className="text-xs text-slate-500" data-testid="amc-filter-count">
                                {visibleItems.length} schedule{visibleItems.length === 1 ? "" : "s"}
                            </span>
                        )}
                        {isPrimaryAdmin && (
                            <ShowArchivedToggle
                                value={showArchived}
                                onChange={setShowArchived}
                                count={items.filter((s) => s.deleted_at).length}
                                testid="amc-show-archived"
                            />
                        )}
                        {/* Iter 2026-02 · Cycle-Expansion toggle. Default =
                            Schedules (one row per master AMC). "Cycles"
                            expands each schedule into its per-cycle rows
                            (Nuvama Quarterly → 4 rows; MFSL Half-Yearly →
                            2 rows) with per-cycle status pills. */}
                        <div
                            className="ml-auto inline-flex items-center rounded-md border border-slate-200 bg-white p-0.5 h-9"
                            role="group"
                            aria-label="AMC view mode"
                            data-testid="amc-view-mode-toggle"
                        >
                            <button
                                type="button"
                                onClick={() => setViewMode("schedules")}
                                className={`px-3 h-8 rounded text-xs font-semibold transition-colors ${viewMode === "schedules" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`}
                                data-testid="amc-view-mode-schedules"
                            >
                                Schedules
                            </button>
                            <button
                                type="button"
                                onClick={() => setViewMode("cycles")}
                                className={`px-3 h-8 rounded text-xs font-semibold transition-colors ${viewMode === "cycles" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`}
                                data-testid="amc-view-mode-cycles"
                            >
                                Cycles
                            </button>
                        </div>
                    </div>
                    {visibleItems.length === 0 ? (
                        <EmptyState
                            icon={<MagnifyingGlass size={40} weight="thin" />}
                            title="No matching AMC schedules"
                            description="Try a different search term or clear the filter."
                            data-testid="amc-no-match"
                        />
                    ) : (
                    <div className="reedos-card">
                        {/* Iter 2026-10-14 — inner scroll context: .reedos-card's
                            overflow-hidden was clipping the wide table with no
                            way to scroll; the wrapper restores horizontal scroll. */}
                        <div className="overflow-x-auto" data-testid="amc-table-scroll" ref={tableScrollRef}>
                        {viewMode === "cycles" ? (
                        <table className="w-full min-w-[1000px] text-sm" data-testid="amc-cycles-table">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr className="text-left">
                                    <th className="px-5 py-3 reedos-eyebrow">Product</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Client</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Frequency</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Cycle</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Period</th>
                                    <th className="px-5 py-3 reedos-eyebrow text-right">Amount</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleItems.flatMap((s) => {
                                    const cycles = expandCycles(s);
                                    const total = cycles.length;
                                    const perCycle = perCycleAmount(s);
                                    return cycles.map((c) => {
                                        const meta = CYCLE_STATUS_META[c.status] || CYCLE_STATUS_META.Upcoming;
                                        return (
                                            <tr
                                                key={`${s.id}-c${c.cycle_number}`}
                                                className={`border-t border-slate-100 reedos-row-hover ${s.deleted_at ? "opacity-60 bg-slate-50/60" : ""}`}
                                                data-testid={`amc-cycle-row-${s.id}-${c.cycle_number}`}
                                            >
                                                <td className="px-5 py-3">
                                                    <div className="font-medium text-slate-900">
                                                        {s.amc_name || s.product_name}
                                                        {s.kind === "subscription" && <span className="ml-1.5 rounded bg-sky-50 border border-sky-200 px-1 py-0.5 text-[9px] font-bold uppercase text-sky-800" data-testid={`amc-kind-subscription-${s.id}`}>Annual Cost</span>}
                                                    </div>
                                                    <div className="text-[11px] text-slate-500 font-mono">{s.po?.po_number || (s.source_type === "manual" ? "Manual" : `PO ${s.po_id || ""}`)}</div>
                                                </td>
                                                <td className="px-5 py-3 text-slate-900">{s.client?.name}</td>
                                                <td className="px-5 py-3 text-slate-700">{s.billing_frequency}</td>
                                                <td className="px-5 py-3">
                                                    <span
                                                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border bg-slate-50 text-slate-700 border-slate-200"
                                                        data-testid={`amc-cycle-num-${s.id}-${c.cycle_number}`}
                                                    >
                                                        {c.cycle_number} / {total}
                                                    </span>
                                                </td>
                                                <td className="px-5 py-3 text-xs text-slate-600">
                                                    {formatDate(c.period_start)} → {formatDate(c.period_end)}
                                                </td>
                                                <td className="px-5 py-3 text-right reedos-data font-semibold">
                                                    {formatINR(c.amount ?? perCycle, s.currency)}
                                                </td>
                                                <td className="px-5 py-3">
                                                    <span
                                                        className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold border ${meta.cls}`}
                                                        data-testid={`amc-cycle-status-${s.id}-${c.cycle_number}`}
                                                    >
                                                        {meta.label}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    });
                                })}
                            </tbody>
                        </table>
                        ) : (
                        <table className="w-full min-w-[1400px] text-sm" data-testid="amc-table">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr className="text-left">
                                    <th className="px-5 py-3 reedos-eyebrow">Product</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Client</th>
                                    {/* POAGG-AMC-001 Phase 3 — dedicated Source column. */}
                                    <th className="px-5 py-3 reedos-eyebrow">Source</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Frequency</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Period</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Next due</th>
                                    <th className="px-5 py-3 reedos-eyebrow text-right">Amount</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Generated</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Status</th>
                                    <th className="px-5 py-3 reedos-eyebrow text-right min-w-[400px]">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleItems.map((s) => {
                                    const effSrc = s.source_type || "po_derived";
                                    const isManual = effSrc === "manual";
                                    const srcMeta = SOURCE_TYPE_META[effSrc] || SOURCE_TYPE_META.po_derived;
                                    const SrcIcon = srcMeta.Icon;
                                    const cmMeta = CREATION_METHOD_META[s.creation_method];
                                    const isOpen = expandedId === s.id;
                                    const preview = previews[s.id];
                                    // Iter 36: prefer calendar-derived effective_status for ALL UI decisions.
                                    // Pause/Resume button still toggles the STORED status field (its source of truth).
                                    const es = effectiveStatus(s);
                                    return (
                                    <Fragment key={s.id}>
                                    <tr className={`border-t border-slate-100 reedos-row-hover ${s.deleted_at ? "opacity-60 bg-slate-50/60" : ""}`} data-testid={`amc-row-${s.id}`}>
                                        <td className="px-5 py-3">
                                            <div className="font-medium text-slate-900 flex items-center gap-2 flex-wrap">
                                                {s.amc_name || s.product_name}
                                                {s.kind === "subscription" && <span className="rounded bg-sky-50 border border-sky-200 px-1 py-0.5 text-[9px] font-bold uppercase text-sky-800" data-testid={`amc-row-kind-subscription-${s.id}`}>Annual Cost</span>}
                                                {s.deleted_at && <ArchivedBadge testid={`amc-archived-badge-${s.id}`} />}
                                            </div>
                                            {isManual ? (
                                                <span className="text-[11px] text-slate-500">No PO linkage</span>
                                            ) : (
                                                <Link to={`/purchase-orders/${s.po_id}`} className="text-[11px] text-slate-500 hover:text-slate-900 font-mono">
                                                    from {s.po?.po_number || `PO ${s.po_id}`}
                                                </Link>
                                            )}
                                            {/* Iter 41: Client PO (externally issued, reconciliation key) */}
                                            {s.external_po_number && (
                                                <div
                                                    className="text-[11px] text-slate-600 mt-0.5 flex items-center gap-1 flex-wrap"
                                                    data-testid={`amc-client-po-${s.id}`}
                                                    title="Client-issued PO reference — used for finance reconciliation. Distinct from any internal/system PO."
                                                >
                                                    <span className="reedos-eyebrow text-[9px] text-slate-400">Client PO</span>
                                                    <span className="font-mono text-slate-700">{s.external_po_number}</span>
                                                    {s.external_po_date && (
                                                        <span className="text-[10px] text-slate-400">· {formatDate(s.external_po_date)}</span>
                                                    )}
                                                </div>
                                            )}
                                            {/* Iter 50: REEDOS Proposal No — originating reference, immutable, additive-only */}
                                            {s.proposal_number && (
                                                <div
                                                    className="text-[11px] text-slate-600 mt-0.5 flex items-center gap-1 flex-wrap"
                                                    data-testid={`amc-reedos-proposal-no-${s.id}`}
                                                    title="REEDOS Proposal No — originating proposal reference, snapshot at AMC creation."
                                                >
                                                    <span className="reedos-eyebrow text-[9px] text-slate-400">REEDOS Proposal No</span>
                                                    {s.proposal_id ? (
                                                        <Link to={`/proposals/${s.proposal_id}`} className="font-mono text-slate-700 hover:text-slate-900 hover:underline">
                                                            {s.proposal_number}
                                                        </Link>
                                                    ) : (
                                                        <span className="font-mono text-slate-700">{s.proposal_number}</span>
                                                    )}
                                                </div>
                                            )}
                                            {/* Iter 28: line-item count + per-line preview when more than one */}
                                            {s.line_items && s.line_items.length > 1 && (
                                                <div className="text-[11px] text-slate-600 mt-1.5 space-y-0.5">
                                                    {s.line_items.slice(0, 3).map((li) => (
                                                        <div key={li.id} className="flex items-center gap-1.5 truncate">
                                                            <span className="text-slate-400">·</span>
                                                            <span className="truncate">{li.product_name}</span>
                                                            <span className="text-slate-400 shrink-0">{formatINR(li.amount, s.currency)} · {li.gst_percent}%</span>
                                                        </div>
                                                    ))}
                                                    {s.line_items.length > 3 && (
                                                        <div className="text-[10px] text-slate-400">+ {s.line_items.length - 3} more line item{s.line_items.length - 3 !== 1 ? "s" : ""}</div>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-5 py-3 text-slate-900">{s.client?.name}</td>
                                        {/* POAGG-AMC-001 Phase 3 — Source column with type chip + creation-method secondary chip. */}
                                        <td className="px-5 py-3" data-testid={`amc-source-cell-${s.id}`}>
                                            <div className="flex flex-col gap-1 items-start">
                                                <span
                                                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border ${srcMeta.cls}`}
                                                    data-testid={`amc-source-${effSrc}-${s.id}`}
                                                    title={srcMeta.title}
                                                >
                                                    <SrcIcon size={10} weight="bold" />
                                                    {srcMeta.label}
                                                </span>
                                                {cmMeta && (
                                                    <span
                                                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-50 text-slate-600 border border-slate-200"
                                                        data-testid={`amc-creation-${s.creation_method}-${s.id}`}
                                                        title={cmMeta.title}
                                                    >
                                                        <cmMeta.Icon size={10} />
                                                        {cmMeta.label}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-5 py-3 text-slate-700">{s.billing_frequency}</td>
                                        <td className="px-5 py-3 text-xs text-slate-600">
                                            <div>{formatDate(s.start_date)} → {formatDate(s.end_date)}</div>
                                            <RenewalNudge endDate={s.end_date} status={es} testId={`amc-renewal-${s.id}`} />
                                            <RenewalStatusChip
                                                sched={s}
                                                testId={`amc-renewal-status-${s.id}`}
                                                onInitiate={() => setRenewalSched(s)}
                                            />
                                        </td>
                                        <td className="px-5 py-3 text-xs">
                                            {/* Iter 59: Calendar Ended still shows next_due (advisory only — billable).
                                                Closed hides next_due entirely (commercial close).
                                                Iter 2026-02: use effectiveNextDue so legacy schedules whose
                                                next_due_date wasn't advanced by scheduler still display the
                                                correct next-cycle date. */}
                                            {es === "Closed" ? (
                                                <span className="text-slate-400">—</span>
                                            ) : (() => {
                                                const eff = effectiveNextDue(s);
                                                if (eff == null) return <span className="text-slate-400">—</span>;
                                                const now = Date.now();
                                                const overdue = eff < now;
                                                return (
                                                    <div className="flex flex-col gap-0.5">
                                                        <span
                                                            className={overdue ? "text-rose-700 font-semibold" : "text-slate-700"}
                                                            data-testid={`amc-next-due-${s.id}`}
                                                        >
                                                            {formatDate(new Date(eff).toISOString())}
                                                        </span>
                                                        {overdue && (
                                                            <span className="text-[10px] text-rose-600" data-testid={`amc-next-due-overdue-${s.id}`}>
                                                                Overdue
                                                            </span>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        </td>
                                        <td
                                            className="px-5 py-3 text-right reedos-data font-semibold"
                                            data-testid={`amc-amount-${s.id}`}
                                            title={s.billing_frequency === "Annual" ? undefined : `Per-cycle amount (annualised ÷ ${MONTHS_PER_CYCLE[s.billing_frequency] ? 12 / MONTHS_PER_CYCLE[s.billing_frequency] : 1}). Contract annual value: ${formatINR(annualisedAmount(s), s.currency)}.`}
                                        >
                                            {/* Iter 2026-02 (user-directed 2026-02-16, Option C) —
                                                Show the per-cycle amount so the row lines up with
                                                the invoice amount the client will actually see.
                                                Annual value still surfaced on hover tooltip and in
                                                the expanded preview panel below the row. */}
                                            {formatINR(perCycleAmount(s), s.currency)}
                                        </td>
                                        <td className="px-5 py-3 text-slate-700 text-xs">{s.generated_count}</td>
                                        <td className="px-5 py-3">
                                            <StatusBadge {...(STATUS_TONE[es] || { status: es })}>{es}</StatusBadge>
                                            {/* Iter 59: surface close reason inline so operators see context at a glance. */}
                                            {es === "Closed" && s.close_reason && (
                                                <div className="text-[10px] text-slate-500 mt-0.5" data-testid={`amc-close-reason-${s.id}`}>
                                                    {s.close_reason}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-5 py-3 text-right min-w-[400px]">
                                            <div className="inline-flex items-center gap-1 flex-wrap justify-end max-w-[400px]">
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => togglePreview(s)}
                                                    className={`gap-1 text-slate-600 hover:text-slate-900 ${isOpen ? "bg-slate-100" : ""}`}
                                                    data-testid={`amc-preview-toggle-${s.id}`}
                                                    title={isOpen ? "Hide invoice preview" : "Preview next invoice"}
                                                >
                                                    <CaretRight size={12} weight="bold" className={`transition-transform ${isOpen ? "rotate-90" : ""}`} />
                                                    <Eye size={13} />
                                                </Button>
                                                {/* Iter 37 + Iter 59: Edit (super admin · manual schedules · not closed) */}
                                                {isManual && canEditManual && es !== "Closed" && (
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() => setEditSched(s)}
                                                        className="gap-1 text-slate-600 hover:text-amber-700"
                                                        data-testid={`amc-edit-${s.id}`}
                                                        title="Edit this manual AMC (affects future invoices only)"
                                                    >
                                                        <Pencil size={13} /> Edit
                                                    </Button>
                                                )}
                                                {/* C.3 Commit 3 — Extend (super admin · PO-derived · not closed · §4.1) */}
                                                {!isManual && canEditManual && es !== "Closed" && (
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() => setExtendSched(s)}
                                                        className="gap-1 text-slate-600 hover:text-indigo-700"
                                                        data-testid={`amc-extend-${s.id}`}
                                                        title="Controlled Extension (§4.1) — forward-only end_date extension with audit and 12-month cumulative cap"
                                                    >
                                                        <Pencil size={13} /> Extend
                                                    </Button>
                                                )}
                                                {/* Iter 59 — Generate is now available for Active / Upcoming / Calendar Ended.
                                                    Calendar Ended is advisory only; the dialog will guide the operator to
                                                    pick a custom period (past-due / extension / reconciliation). Paused and
                                                    Closed remain blocked (operator-set states). */}
                                                {canBillOps && (es === "Active" || es === "Upcoming" || es === "Calendar Ended") && (
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() => setGenerateSched(s)}
                                                        className="gap-1 text-amber-700 hover:text-amber-900"
                                                        data-testid={`amc-generate-now-${s.id}`}
                                                        title={
                                                            es === "Calendar Ended"
                                                                ? "Contract end-date has passed (advisory). Pick a custom period to bill past-due / extension cycles."
                                                                : es === "Upcoming"
                                                                ? "Generate an advance invoice (contract has not started yet — adjust the billing period if needed)"
                                                                : "Manually generate the next invoice"
                                                        }
                                                    >
                                                        <Lightning size={13} weight="fill" /> Generate
                                                    </Button>
                                                )}
                                                {/* Iter 37 + Iter 59: Record Offline Invoice (super admin + accounts · not Closed) */}
                                                {canBillOps && es !== "Closed" && (
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() => setOfflineSched(s)}
                                                        className="gap-1 text-slate-700 hover:text-slate-900"
                                                        data-testid={`amc-record-offline-${s.id}`}
                                                        title="Record an invoice raised outside the system (Tally / SAP / Excel)"
                                                    >
                                                        <Receipt size={13} /> Offline
                                                    </Button>
                                                )}
                                                <RoleGuard permission="po:write">
                                                    {/* Iter 59: Pause/Resume blocked when commercially Closed. */}
                                                    {es !== "Closed" && (
                                                        <Button size="sm" variant="ghost" onClick={() => toggle(s)} className={s.status === "Active" ? "text-amber-700 gap-1" : "text-emerald-700 gap-1"} data-testid={`amc-toggle-${s.id}`}>
                                                            {s.status === "Active" ? <><PauseCircle size={14} /> Pause</> : <><PlayCircle size={14} /> Resume</>}
                                                        </Button>
                                                    )}
                                                </RoleGuard>
                                                {/* Iter 59 — Close / Reopen (Primary Admin only). The single
                                                    explicit operator action that ends a contract's billable
                                                    lifecycle. Calendar end-date never auto-closes. */}
                                                {isPrimaryAdmin && !s.deleted_at && (
                                                    s.status === "Closed" ? (
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={() => setCloseSched(s)}
                                                            className="gap-1 text-emerald-700 hover:text-emerald-900"
                                                            data-testid={`amc-reopen-${s.id}`}
                                                            title="Reopen this commercially closed schedule"
                                                        >
                                                            <LockOpen size={13} /> Reopen
                                                        </Button>
                                                    ) : (
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={() => setCloseSched(s)}
                                                            className="gap-1 text-slate-700 hover:text-rose-700"
                                                            data-testid={`amc-close-${s.id}`}
                                                            title="Commercially close (discontinue) this schedule — hard-blocks future billing"
                                                        >
                                                            <Lock size={13} /> Close
                                                        </Button>
                                                    )
                                                )}
                                                {/* Iter 51.1 — Archive / Unarchive (shared component).
                                                    Iter 51.2 — gated to primary super-admin only. */}
                                                {isPrimaryAdmin && (
                                                    s.deleted_at
                                                        ? <UnarchiveRowButton onClick={() => archive.openUnarchive(s)} testid={`amc-unarchive-${s.id}`} />
                                                        : <ArchiveRowButton onClick={() => archive.openArchive(s)} testid={`amc-archive-${s.id}`} />
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                    {isOpen && (
                                        <tr className="bg-slate-50/60 border-t border-slate-100" data-testid={`amc-preview-row-${s.id}`}>
                                            <td colSpan={10} className="px-5 py-4">
                                                <AmcInvoicePreview state={preview} sched={s} />
                                            </td>
                                        </tr>
                                    )}
                                    </Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                        )}
                        </div>
                        <PinnedScrollbar targetRef={tableScrollRef} testid="amc-pinned-scrollbar" />
                        <StickyTableHeader targetRef={tableScrollRef} testid="amc-sticky-header" />
                    </div>
                    )}
                    </>
                )}
            </div>
            <CreateManualAmcDialog
                open={createOpen}
                onOpenChange={setCreateOpen}
                onCreated={async () => { await load(); }}
            />
            <EditManualAmcDialog
                open={!!editSched}
                onOpenChange={(v) => !v && setEditSched(null)}
                sched={editSched}
                onSaved={async () => {
                    setEditSched(null);
                    // Bust any cached preview for this schedule so the next
                    // expand re-fetches with the updated line items.
                    if (editSched?.id) {
                        setPreviews((p) => {
                            const { [editSched.id]: _drop, ...rest } = p;
                            return rest;
                        });
                    }
                    await load();
                }}
            />
            <ExtendAmcEndDateDialog
                open={!!extendSched}
                onOpenChange={(v) => !v && setExtendSched(null)}
                sched={extendSched}
                onSaved={async () => {
                    if (extendSched?.id) {
                        setPreviews((p) => {
                            const { [extendSched.id]: _drop, ...rest } = p;
                            return rest;
                        });
                    }
                    setExtendSched(null);
                    await load();
                }}
            />
            <InitiateRenewalDialog
                open={!!renewalSched}
                onOpenChange={(v) => !v && setRenewalSched(null)}
                sched={renewalSched}
                onInitiated={async () => {
                    setRenewalSched(null);
                    await load();
                }}
            />
            <GenerateAmcInvoiceNowDialog
                open={!!generateSched}
                onOpenChange={(v) => !v && setGenerateSched(null)}
                sched={generateSched}
                onGenerated={async () => { setGenerateSched(null); await load(); }}
            />
            <RecordOfflineInvoiceDialog
                open={!!offlineSched}
                onOpenChange={(v) => !v && setOfflineSched(null)}
                sched={offlineSched}
                onRecorded={async () => { setOfflineSched(null); await load(); }}
            />
            <CloseAmcDialog
                open={!!closeSched}
                onOpenChange={(v) => !v && setCloseSched(null)}
                sched={closeSched}
                onDone={async () => { setCloseSched(null); await load(); }}
            />

            {/* Iter 51.1 — Archive/Unarchive confirm dialog (shared) */}
            {archive.dialog}
        </div>
    );
}


/**
 * Iter 32: Inline read-only preview of the NEXT invoice this AMC schedule
 * will emit. Lightweight, verification-oriented — NOT a duplicate invoice
 * editor. The values match the persisted invoice byte-for-byte because the
 * same `build_amc_invoice_preview` builder powers both the scheduler job and
 * this endpoint.
 */
function AmcInvoicePreview({ state, sched }) {
    if (state === "loading") {
        return <div className="text-xs text-slate-500" data-testid="amc-preview-loading">Building preview…</div>;
    }
    if (state === "error" || !state) {
        return <div className="text-xs text-rose-600" data-testid="amc-preview-error">Couldn't load preview. Try again.</div>;
    }
    // state is the API response: { preview, reason, preview_paused? }
    const preview = state.preview || state.preview_paused;
    const reason = state.reason;
    if (!preview) {
        return (
            <div className="text-xs text-slate-500 italic" data-testid="amc-preview-empty">
                {reason || "No upcoming invoice can be previewed for this schedule."}
            </div>
        );
    }
    const isPaused = !state.preview && !!state.preview_paused;

    return (
        <div className="space-y-3" data-testid="amc-preview-body">
            <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-slate-100 text-slate-700 border border-slate-200">
                    <Eye size={11} weight="bold" /> Preview · not yet generated
                </span>
                {isPaused && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200">
                        Schedule is paused — values shown reflect resume
                    </span>
                )}
                <span className="text-[11px] text-slate-500">
                    Mirrors the format of the persisted AMC invoice. Reads only — generates when due.
                </span>
            </div>

            <div className="rounded-md border border-slate-200 bg-white overflow-hidden">
                <table className="w-full text-xs">
                    <thead className="bg-slate-50 border-b border-slate-200">
                        <tr className="text-left">
                            <th className="px-3 py-2 reedos-eyebrow w-[44%]">Description</th>
                            <th className="px-3 py-2 reedos-eyebrow text-right">Amount</th>
                            <th className="px-3 py-2 reedos-eyebrow text-right">GST</th>
                            <th className="px-3 py-2 reedos-eyebrow text-right">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {preview.line_items.map((li, idx) => (
                            <tr key={idx} className="border-t border-slate-100 align-top">
                                <td className="px-3 py-2 text-slate-900">
                                    <div className="font-medium">{li.description}</div>
                                    {li.is_amc_period && (
                                        <div className="text-[11px] text-slate-500 mt-0.5">
                                            <div>{li.service_charge_label}</div>
                                            <div>
                                                Service Period: {formatDate(li.service_period_start)} → {formatDate(li.service_period_end)}
                                            </div>
                                        </div>
                                    )}
                                </td>
                                <td className="px-3 py-2 text-right reedos-data">{formatINR(li.amount, sched?.currency)}</td>
                                <td className="px-3 py-2 text-right reedos-data text-slate-600">
                                    {formatINR(li.gst_amount, sched?.currency)}
                                    <span className="text-[10px] text-slate-400 ml-1">({li.gst_percent}%)</span>
                                </td>
                                <td className="px-3 py-2 text-right reedos-data font-semibold">{formatINR(li.total_amount, sched?.currency)}</td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot className="border-t border-slate-200 bg-slate-50/50">
                        <tr>
                            <td className="px-3 py-2 reedos-eyebrow text-right" colSpan={3}>Subtotal</td>
                            <td className="px-3 py-2 reedos-data text-right">{formatINR(preview.subtotal, sched?.currency)}</td>
                        </tr>
                        <tr>
                            <td className="px-3 py-2 reedos-eyebrow text-right" colSpan={3}>GST</td>
                            <td className="px-3 py-2 reedos-data text-right">{formatINR(preview.tax_amount, sched?.currency)}</td>
                        </tr>
                        <tr className="border-t border-slate-200">
                            <td className="px-3 py-2 font-semibold text-right text-slate-900" colSpan={3}>Total payable</td>
                            <td className="px-3 py-2 reedos-data text-right font-bold text-slate-900">{formatINR(preview.total_amount, sched?.currency)}</td>
                        </tr>
                    </tfoot>
                </table>
            </div>

            <div className="flex items-center gap-4 text-[11px] text-slate-500 flex-wrap">
                <span className="inline-flex items-center gap-1">
                    <Calendar size={11} /> Next due {formatDate(preview.next_due_date)}
                </span>
                <span>·</span>
                <span>Payment terms: <b className="text-slate-700">{preview.payment_terms}</b></span>
                <span>·</span>
                <span>Frequency: <b className="text-slate-700">{preview.billing_frequency}</b></span>
                {preview.is_due_now && (
                    <span className="text-amber-700 font-semibold">· Due now — will generate at next scheduler tick</span>
                )}
                {sched.last_invoice_id && (
                    <>
                        <span>·</span>
                        <Link to={`/invoices/${sched.last_invoice_id}`} className="text-slate-700 hover:text-slate-900 font-semibold underline-offset-2 hover:underline" data-testid={`amc-preview-last-link-${sched.id}`}>
                            View last issued invoice →
                        </Link>
                    </>
                )}
            </div>
        </div>
    );
}

/**
 * Iter 33: Subtle renewal-window nudge.
 *
 * Pure presentation — no API call, just derives days-to-expiry from the
 * schedule end_date already on the row. Hidden when:
 *   - status is Expired (the Status badge already covers it)
 *   - end_date is missing
 *   - > 90 days remain (avoids clutter — too early to matter)
 *
 * Visual tone is deliberately neutral until the last 30 days, when it shifts
 * to amber (NOT rose) — operational continuity context, not an alarm.
 */
function RenewalNudge({ endDate, status, testId }) {
    // Iter 59 — hide for Closed (operator-locked) and Calendar Ended (the
    // Status badge already covers the past-date state).
    if (!endDate || status === "Closed" || status === "Calendar Ended" || status === "Expired") return null;
    const end = new Date(endDate);
    if (Number.isNaN(end.getTime())) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endMid = new Date(end);
    endMid.setHours(0, 0, 0, 0);
    const days = Math.round((endMid - today) / 86400000);
    if (days > 90) return null;

    let label;
    if (days < 0) {
        label = `Renewal overdue · ended ${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} ago`;
    } else if (days === 0) {
        label = "Renews today";
    } else if (days === 1) {
        label = "Renews tomorrow";
    } else if (days <= 30) {
        label = `Renews in ${days} days`;
    } else {
        label = `Renewal opens in ${days} days`;
    }

    const tone = days <= 30
        ? "bg-amber-50 text-amber-800 border-amber-200"
        : "bg-slate-50 text-slate-600 border-slate-200";

    return (
        <div
            className={`inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded text-[10px] font-medium border ${tone}`}
            data-testid={testId}
            title={`Contract end: ${endDate}`}
        >
            <Clock size={10} weight={days <= 30 ? "fill" : "regular"} />
            <span>{label}</span>
        </div>
    );
}


/**
 * RenewalStatusChip (C.3 Commit 4)
 *
 * Shows the AMC's lifecycle renewal_status as a coloured chip + an
 * "Initiate Renewal" CTA when the status is actionable.
 *
 *   not_due             → no chip (silent — operator sees nothing)
 *   renewal_due         → amber chip + Initiate Renewal CTA
 *   calendar_ended      → rose chip + Initiate Renewal CTA (overdue)
 *   renewal_in_progress → indigo chip with proposal-number deep-link
 *   renewed_and_closed  → emerald chip with succeeded-by AMC link
 */
function RenewalStatusChip({ sched, testId, onInitiate }) {
    const status = sched?.renewal_status;
    if (!status || status === "not_due") return null;

    const propNumber = sched.renewal_proposal_number;
    const propId = sched.renewal_proposal_id;
    const succeededBy = sched.succeeded_by;

    if (status === "renewal_in_progress") {
        return (
            <div
                className="inline-flex items-center gap-1.5 mt-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border bg-indigo-50 text-indigo-800 border-indigo-200"
                data-testid={testId}
                title={`Renewal in progress — proposal ${propNumber || ""}`}
            >
                <span className="uppercase tracking-wider">Renewal in progress</span>
                {propId && propNumber && (
                    <Link
                        to={`/proposals/${propId}`}
                        className="font-mono underline-offset-2 hover:underline"
                    >
                        {propNumber}
                    </Link>
                )}
            </div>
        );
    }

    if (status === "renewed_and_closed") {
        return (
            <div
                className="inline-flex items-center gap-1.5 mt-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border bg-emerald-50 text-emerald-800 border-emerald-200"
                data-testid={testId}
                title="This AMC was renewed and closed; see succeeded_by for the live schedule."
            >
                <span className="uppercase tracking-wider">Renewed</span>
                {succeededBy && (
                    <Link
                        to={`/amc/${succeededBy}`}
                        className="font-mono underline-offset-2 hover:underline"
                    >
                        → {succeededBy}
                    </Link>
                )}
            </div>
        );
    }

    // renewal_due or calendar_ended → actionable chip + Initiate CTA.
    const isOverdue = status === "calendar_ended";
    const tone = isOverdue
        ? "bg-rose-50 text-rose-800 border-rose-200"
        : "bg-amber-100 text-amber-900 border-amber-300";
    const label = isOverdue ? "Renewal overdue" : "Renewal due";
    const days = typeof sched.days_until_end === "number" ? sched.days_until_end : null;

    return (
        <div className="inline-flex items-center gap-1.5 mt-1 flex-wrap" data-testid={testId}>
            <span
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border uppercase tracking-wider ${tone}`}
            >
                {label}
                {days !== null && days >= 0 && (
                    <span className="font-normal normal-case">— {days}d to end</span>
                )}
                {days !== null && days < 0 && (
                    <span className="font-normal normal-case">— ended {Math.abs(days)}d ago</span>
                )}
            </span>
            {onInitiate && (
                <button
                    type="button"
                    onClick={onInitiate}
                    className="text-[10px] font-medium text-indigo-700 hover:text-indigo-900 hover:underline"
                    data-testid={`${testId}-initiate-btn`}
                >
                    Initiate Renewal
                </button>
            )}
        </div>
    );
}

